import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/gallery", label: "Gallery" },
  { to: "/wholesale", label: "Wholesale" },
  { to: "/about", label: "About" },
  { to: "/coming-soon", label: "New Brand" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed top-0 left-0 right-0 z-50 bg-ivory/85 backdrop-blur-md border-b border-line"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-3 group">
            <span className="font-serif text-2xl tracking-tight">Capstone</span>
            <span className="eyebrow text-smoke hidden sm:inline">Hats LTD — Est. UK</span>
          </Link>

          <nav className="hidden md:flex items-center gap-9" data-testid="nav-desktop">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `nav-link eyebrow !text-[0.625rem] text-ink/80 hover:text-ink transition-colors duration-300 ${
                    isActive ? "active text-ink" : ""
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-enquire-cta"
              className="eyebrow !text-[0.625rem] bg-green text-ivory px-5 py-3 hover:bg-ink transition-colors duration-300"
            >
              Enquire
            </Link>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden p-2"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={22} strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            className="fixed inset-0 z-[80] bg-green text-ivory flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="h-20 px-6 flex items-center justify-between border-b border-ivory/15">
              <span className="font-serif text-2xl">Capstone</span>
              <button
                data-testid="nav-mobile-close"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="p-2"
              >
                <X size={24} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.07, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={l.to}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                    onClick={() => setOpen(false)}
                    className={`font-serif text-4xl py-3 block transition-colors duration-300 ${
                      location.pathname === l.to ? "italic text-honey" : "hover:text-honey"
                    }`}
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <p className="eyebrow text-ivory/50 px-8 pb-10">Tradition in every stitch.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
